import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex, Heading, VStack, IconButton, useColorModeValue, Text, Divider, HStack } from "@chakra-ui/react";
import SidebarBtn from "./dashboardBtn";
import ReportsDropdown from "./ReportsDropdown";
import PayrollDropdown from "./PayrollDropdown";
import { FiX, FiDownload } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { ROUTES } from "../constant";
import { useUserContext } from "../context/UserContext";
import { usePlanRestriction } from "../context/PlanRestrictionContext";

const SideBar = ({ sidebarOpen, setSidebarOpen }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  
  // Only use UserContext when not on login pages
  const { isAdmin, canManageBranches, canManageUsers, canAccessPayroll, isPayrollViewer, isHRManager, isAccountant, isPayrollAdmin } = useUserContext();
  const { canAccessFeature } = usePlanRestriction();
  
  // Don't render sidebar on login or reset password pages
  if (pathname === '/login' || pathname === '/reset-password') {
    return null;
  }

  const handleDownloadAPK = () => {
    // Replace with your actual APK download URL
    const apkUrl = "https://your-domain.com/ems-app.apk";
    window.open(apkUrl, '_blank');
  };

  return (
    <Flex
      bg={bgColor}
      h="100vh"
      flexDirection="column"
      p={6}
      display={{ base: sidebarOpen ? "block" : "none", lg: "block" }}
      w="100%"
      maxW="100%"
      overflowX="hidden"
      overflowY="auto"
      borderRight="1px solid"
      borderColor={borderColor}
      shadow="sm"
    >
      {/* Mobile Header */}
      <Flex
        display={{ base: "flex", lg: "none" }}
        justifyContent="space-between"
        alignItems="center"
        mb={8}
      >
        <Heading fontSize="lg" color="gray.900" fontWeight="600">
          E-MANAGEMENT
        </Heading>
        <IconButton
          icon={<FiX />}
          variant="ghost"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar"
          color="gray.600"
          _hover={{ bg: "gray.100" }}
        />
      </Flex>

      {/* Desktop Logo */}
      <Box display={{ base: "none", lg: "block" }} mb={8}>
        <Flex align="center" justify="center">
          <Box
            p={3}
            borderRadius="12px"
            bg="blue.50"
            color="blue.600"
            fontSize="2xl"
            fontWeight="bold"
          >
            EM
          </Box>
        </Flex>
      </Box>

      {/* Welcome Section */}
      <VStack spacing={6} align="stretch" mb={8}>
        <Heading fontSize="md" color="gray.900" fontWeight="600">
          {t('navigation.welcome')}
        </Heading>
        <Text fontSize="sm" color="gray.600">
          {t('dashboard.subtitle')}
        </Text>
      </VStack>

      {/* Navigation */}
      <VStack spacing={2} align="stretch" flex={1}>
        <SidebarBtn
          text={t('common.dashboard')}
          path={ROUTES.dashboard}
          currentPath={pathname}
        />
        <SidebarBtn
          text={t('common.employees')}
          path={ROUTES.employee}
          currentPath={pathname}
        />
        {/* Configuration - Only for Company Admin and non-payroll users */}
        {(isAdmin() || (!isPayrollViewer() && !isHRManager() && !isAccountant() && !isPayrollAdmin())) && (
          <SidebarBtn
            text={t('common.configuration')}
            path={ROUTES.configuration}
            currentPath={pathname}
          />
        )}
        {/* Reports - Hidden for Accountants (Payroll Reports available in Payroll menu) */}
        {!isAccountant() && <ReportsDropdown />}
        {/* Payroll Dropdown - Main Branch users only */}
        {canAccessPayroll() && <PayrollDropdown />}
        {/* Recent Entries - Hidden for Accountants */}
        {!isAccountant() && (
          <SidebarBtn
            text={t('common.recentEntries')}
            path={ROUTES.entries}
            currentPath={pathname}
          />
        )}
        {/* Leave - Hidden for Accountants (HR function only) */}
        {!isAccountant() && (
          <SidebarBtn
            text={t('common.leave')}
            path="/leave"
            currentPath={pathname}
          />
        )}
        {/* Settings - Only for Company Admin and non-payroll users */}
        {(isAdmin() || (!isPayrollViewer() && !isHRManager() && !isAccountant() && !isPayrollAdmin())) && (
          <SidebarBtn
            text="Settings"
            path="/settings"
            currentPath={pathname}
          />
        )}
        
        {/* Admin Section */}
        {isAdmin() && (
          <>
            <Divider my={4} />
            <Text fontSize="xs" fontWeight="600" color="gray.500" px={3} mb={2}>
              ADMIN
            </Text>
            {canManageBranches && (
              <SidebarBtn
                text="Branch Management"
                path="/admin/branches"
                currentPath={pathname}
              />
            )}
            {canManageUsers && (
              <SidebarBtn
                text="User Management"
                path="/admin/users"
                currentPath={pathname}
              />
            )}
              <SidebarBtn
                text="Overtime & Location Management"
                path="/admin/overtime"
                currentPath={pathname}
              />
          </>
        )}
      </VStack>

      {/* Mobile App Download Section */}
      <Box mt={6} p={4} bg="blue.50" borderRadius="12px" border="1px solid" borderColor="blue.200">
        <VStack spacing={3} align="stretch">
          <HStack spacing={3}>
            <Box
              p={2}
              borderRadius="8px"
              bg="blue.100"
              color="blue.600"
            >
              <FiDownload size={16} />
            </Box>
            <Text fontSize="sm" fontWeight="600" color="blue.900">
              {t('navigation.mobileAppDownload')}
            </Text>
          </HStack>
          <Text fontSize="xs" color="blue.700">
            {t('navigation.mobileAppDescription')}
          </Text>
          <Box
            as="button"
            onClick={handleDownloadAPK}
            p={2}
            bg="blue.600"
            color="white"
            borderRadius="8px"
            fontSize="xs"
            fontWeight="500"
            _hover={{ bg: "blue.700" }}
            transition="all 0.2s"
            textAlign="center"
          >
            {t('common.download')} APK
          </Box>
        </VStack>
      </Box>

      {/* Footer */}
      <Box mt="auto" pt={6} borderTop="1px solid" borderColor="gray.200">
        <Text fontSize="xs" color="gray.500" textAlign="center">
          Employee Management System
        </Text>
      </Box>
    </Flex>
  );
};

export default SideBar;
